import type { Submenu } from '@hooks/useFetchMenu'

export const RELAX_MENU_KEY = 'RELAXMENU'

/** Códigos base RELAX conocidos; fallback si el menú aún no cargó. */
export const RELAX_DEFAULT_BASE_CODES = new Set(['167', '172'])

export const RELAX_ARM_POSITIONS = ['D', 'A', 'I'] as const

export type RelaxArmPosition = (typeof RELAX_ARM_POSITIONS)[number]

export type RelaxArmVariantParts = {
  baseCode: string
  brNumber: number
  position: RelaxArmPosition
}

// Acepta «167 BR3 D», «167-105 BR3 D» y prefijo de altura «47 167 BR3 D».
const RELAX_VARIANT_CATALOG_PATTERN = /^(?:\d{2}\s+)?(\d+)(?:-\d{2,3})?\s+BR(\d+)\s+([DAI])$/i
const RELAX_VARIANT_MENU_KEY_PATTERN = /^(\d+)BR(\d+)([DAI])$/i

const normalizeRelaxCatalog = (catalog: string): string =>
  catalog.trim().replace(/\s+/g, ' ').toUpperCase()

const readSubmenuCatalogCode = (item: Submenu): string => {
  const args = item.args
  const fromArgs = Array.isArray(args) ? args[0] : args
  return String(item.codigoUnidad || fromArgs || item.claveMenuItem || '').trim()
}

export function parseRelaxArmVariantCatalog(catalogID: unknown): RelaxArmVariantParts | null {
  const match = normalizeRelaxCatalog(String(catalogID || '')).match(RELAX_VARIANT_CATALOG_PATTERN)
  if (!match) return null

  const position = match[3].toUpperCase() as RelaxArmPosition
  if (!RELAX_ARM_POSITIONS.includes(position)) return null

  return {
    baseCode: match[1],
    brNumber: Number(match[2]),
    position,
  }
}

export function getRelaxBaseCode(catalogID: unknown): string | null {
  const variant = parseRelaxArmVariantCatalog(catalogID)
  if (variant) return variant.baseCode

  // Quitar prefijo de altura de asiento («47 167») y sufijo de fondo («167-105»).
  const raw = String(catalogID || '')
    .trim()
    .replace(/^\d{2}\s+/, '')
    .replace(/^(\d+)-\d{2,3}\b/i, '$1')

  const exactBaseMatch = raw.match(/^(\d+)$/)
  if (exactBaseMatch) return exactBaseMatch[1]

  for (const baseCode of RELAX_DEFAULT_BASE_CODES) {
    const upper = raw.toUpperCase()
    if (upper === baseCode || upper.startsWith(`${baseCode} BR`) || upper.startsWith(`${baseCode} `)) {
      return baseCode
    }
  }

  return null
}

export function isRelaxArmVariantMenuItem(item: Submenu): boolean {
  const catalogCode = normalizeRelaxCatalog(readSubmenuCatalogCode(item))
  if (RELAX_VARIANT_CATALOG_PATTERN.test(catalogCode)) return true

  const menuKey = String(item.claveMenuItem || '').trim()
  return RELAX_VARIANT_MENU_KEY_PATTERN.test(menuKey)
}

export function collectRelaxBaseCodes(submenus: Submenu[]): Set<string> {
  const baseCodes = new Set<string>()
  submenus.forEach((item) => {
    if (isRelaxArmVariantMenuItem(item)) return
    const code = String(item.codigoUnidad || item.claveMenuItem || '').trim()
    if (/^\d+$/.test(code)) baseCodes.add(code)
  })
  return baseCodes
}

export function filterRelaxMenuBaseUnits(submenus: Submenu[]): Submenu[] {
  return submenus.filter((item) => !isRelaxArmVariantMenuItem(item))
}

export function filterMenuSubmenusForToolbar(menuKey: string, submenus: Submenu[]): Submenu[] {
  if (menuKey.toUpperCase() === RELAX_MENU_KEY) {
    return filterRelaxMenuBaseUnits(submenus)
  }

  return submenus.filter(
    (item) =>
      !String(item.claveMenuItem || '').startsWith('BR') && !isRelaxArmVariantMenuItem(item),
  )
}

export function isRelaxIntegratedUnit(
  catalogID: unknown,
  relaxBaseCodes: ReadonlySet<string>,
): boolean {
  const baseCode = getRelaxBaseCode(catalogID)
  if (baseCode == null) return false
  if (relaxBaseCodes.has(baseCode)) return true
  return RELAX_DEFAULT_BASE_CODES.has(baseCode)
}

export function buildRelaxArmVariantCatalog(
  baseCode: string,
  brNumber: number,
  position: RelaxArmPosition,
): string {
  return `${baseCode} BR${brNumber} ${position}`
}

/**
 * Brazos integrados de una unidad Relax para presupuesto:
 * - BRx I / D → 1 brazo
 * - BRx A → 2 brazos
 * - sin variante → null (no suma)
 */
export function getRelaxIntegratedArmBudgetContribution(
  catalogID: unknown,
): { armRef: string; count: number } | null {
  const variant = parseRelaxArmVariantCatalog(catalogID)
  if (!variant) return null
  const count = variant.position === 'A' ? 2 : 1
  return {
    armRef: `B${variant.brNumber}`,
    count,
  }
}

export function cycleRelaxArmPosition(current: RelaxArmPosition): RelaxArmPosition {
  const currentIndex = RELAX_ARM_POSITIONS.indexOf(current)
  const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % RELAX_ARM_POSITIONS.length
  return RELAX_ARM_POSITIONS[nextIndex]
}

/** Ciclo RELAX: sin brazos → D → A → I → sin brazos (mismo BR). */
export function resolveRelaxArmVariantCatalog(
  currentCatalogID: unknown,
  clickedBrNumber: number,
): string | null {
  const baseCode = getRelaxBaseCode(currentCatalogID)
  if (!baseCode) return null

  const currentParts = parseRelaxArmVariantCatalog(currentCatalogID)

  if (!currentParts) {
    return buildRelaxArmVariantCatalog(baseCode, clickedBrNumber, 'D')
  }

  if (currentParts.brNumber !== clickedBrNumber) {
    return buildRelaxArmVariantCatalog(baseCode, clickedBrNumber, currentParts.position)
  }

  if (currentParts.position === 'I') {
    return baseCode
  }

  const nextPosition = cycleRelaxArmPosition(currentParts.position)
  return buildRelaxArmVariantCatalog(baseCode, clickedBrNumber, nextPosition)
}

export function findRelaxBaseUnitInMenu(submenus: Submenu[], baseCode: string): Submenu | null {
  const normalizedBase = String(baseCode || '').trim()
  if (!normalizedBase) return null

  return (
    submenus.find((item) => {
      if (isRelaxArmVariantMenuItem(item)) return false
      const code = String(
        item.codigoUnidad || readSubmenuCatalogCode(item) || item.claveMenuItem || '',
      ).trim()
      return code === normalizedBase
    }) ?? null
  )
}

export function findRelaxArmVariantInMenu(
  submenus: Submenu[],
  targetCatalogID: string,
): Submenu | null {
  const normalizedTarget = normalizeRelaxCatalog(targetCatalogID)

  return (
    submenus.find((item) => {
      const catalogCode = normalizeRelaxCatalog(readSubmenuCatalogCode(item))
      return catalogCode === normalizedTarget
    }) ?? null
  )
}

export function parseRelaxArmButtonCode(claveMenuItem: string): number | null {
  const match = String(claveMenuItem || '')
    .trim()
    .toUpperCase()
    .match(/^BR(\d+)$/)
  if (!match) return null

  const brNumber = Number(match[1])
  return Number.isFinite(brNumber) && brNumber >= 1 && brNumber <= 5 ? brNumber : null
}

/** catalogID o referencia del ítem en escena (linkedItem). */
export function getRelaxCatalogFromSceneItem(
  item:
    | {
        catalogID?: unknown
        referencia?: unknown
      }
    | null
    | undefined,
): string {
  return String(item?.catalogID || item?.referencia || '').trim()
}

export function isRelaxIntegratedSceneItem(
  item: { catalogID?: unknown; referencia?: unknown } | null | undefined,
  relaxBaseCodes: ReadonlySet<string>,
): boolean {
  return isRelaxIntegratedUnit(getRelaxCatalogFromSceneItem(item), relaxBaseCodes)
}

const EMPTY_RELAX_BASE_CODES: ReadonlySet<string> = new Set<string>()

/**
 * Variante de `isRelaxIntegratedUnit` que solo usa los códigos base por defecto
 * (167/172). Pensada para contextos sin acceso al menú (utilidades de escena),
 * donde el brazo RELAX se gestiona por cambio de GLB y nunca debe recibir un
 * brazo BR independiente.
 */
export function isRelaxIntegratedUnitByDefaults(catalogID: unknown): boolean {
  return isRelaxIntegratedUnit(catalogID, EMPTY_RELAX_BASE_CODES)
}
